import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider } from "@tanstack/react-router";
import i18n from "i18next";

import { AppErrorBoundary } from "@/app/errors/AppErrorBoundary";
import { createAppRouter } from "@/app/router";
import { createQueryClient } from "@/shared/api/query-client";
import { hasSession, subscribeSession } from "@/shared/auth/session";
import { clearPrivateSearches } from "@/shared/lib/private-search";

const queryClient = createQueryClient();
const router = createAppRouter(queryClient);

// Signing in or out, in this tab or another, re-runs the route guards: they
// send a signed-out person to the sign-in page and a signed-in one away from
// it. What the last person loaded, and whom they searched for, is dropped the
// moment they are signed out.
let signedIn = hasSession();
subscribeSession(() => {
  if (hasSession() === signedIn) return;
  signedIn = hasSession();
  if (!signedIn) {
    queryClient.clear();
    clearPrivateSearches();
  }
  void router.invalidate();
});

// Texts the services wrote (names, titles of problems) are in the language
// they were asked in: ask again.
i18n.on("languageChanged", () => {
  void queryClient.invalidateQueries();
});

export function App() {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        {import.meta.env.DEV && import.meta.env.VITE_DEVTOOLS !== "off" && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
